import { HttpClient, HttpHeaders, HttpBackend } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IconResponse } from "app/models/icons.model";
import { environment } from 'environments/environment';
import { CrAlert } from './crsync.service';

export interface IconType {
  alarmGroup: string;
  alarmNode: string;
  devicePrefixClass: string;
  alarmType?: string;
};

@Injectable({
  providedIn: 'root'
})

export class IconsService {

  private http: HttpClient;
  private readonly fallbackIcon: IconResponse = {
    iconClass: "fa-solid fa-bell",
    color: "#707070",
    schema_version: '3'
  };

  constructor(
    backend: HttpBackend
  ) { 
    // Use pristine backend so portal interceptor headers are not added to alert server calls.
    this.http = new HttpClient(backend);
  }

  private readonly iconCache = new Map<string, IconResponse>();
  private asBaseUrl = environment.alertServerBaseUrl;

  findIcon(alert: CrAlert): Promise<IconResponse> {
    const obj: IconType = {
      alarmGroup: alert.alertGroup.toString(),
      alarmNode: alert.alertNode.toString(),
      devicePrefixClass: alert.deviceType.toString(),
      alarmType: alert.alarmType.toString() || 'TYPE_NONE'
    };

    /** Use cache to avoid redundant HTTP requests */
    const cacheKey = this.getCacheKey(obj);
    const cachedIcon = this.iconCache.get(cacheKey);
    if (cachedIcon) {
      return Promise.resolve(cachedIcon);
    }

    // Alert server credentials may not be ready yet (or user may be logged out).
    // Avoid an unnecessary 401 request and return the default icon immediately.
    if (!this.hasAlertServerCredentials()) {
      return Promise.resolve(this.fallbackIcon);
    }

    return new Promise((resolve) => {
      this.getIcon(obj).subscribe({
        next: (res: IconResponse) => {
          const iconResponse = res.iconClass ? res : this.fallbackIcon;
          this.iconCache.set(cacheKey, iconResponse);
          resolve(iconResponse);
        },
        error: () => {
          this.iconCache.set(cacheKey, this.fallbackIcon);
          resolve(this.fallbackIcon);
        }
      });
    });
  }

  private hasAlertServerCredentials(): boolean {
    const alertServerUserRaw = localStorage.getItem('alertServerUser');
    if (!alertServerUserRaw) {
      return false;
    }

    try {
      const asUser = JSON.parse(alertServerUserRaw);
      return !!asUser?.authToken && !!asUser?.companyId;
    } catch {
      return false;
    }
  }

  private getCacheKey(iconType: IconType): string {
    return [
      iconType.alarmGroup,
      iconType.alarmNode,
      iconType.devicePrefixClass,
      iconType.alarmType || ''
    ].join('|');
  }

  getIcon(body: IconType) {
    const url = this.asBaseUrl + '/api/resources/icons';
    const asUser = JSON.parse(localStorage.getItem('alertServerUser') || '{}');
    const options = {
      headers: new HttpHeaders({
        'x-alertsrv-authtoken': asUser.authToken || '',
        'x-alertsrv-sessionid': asUser.authToken || '',
        'x-everon-companyid': asUser.companyId || ''
      })
    };
    return this.http.post<IconResponse>(url, body, options);
  }
}
